import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiAlertCircle, FiEye, FiEyeOff } from "react-icons/fi";
import "./SignIn.css";
import API_URL from "./Config";
import { fetchUserMenu, getDefaultMenuPath } from "./services/menuService";

const LOGIN_REDIRECT_FALLBACK = "/datalist";

const SignIn = ({ onLogin }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const getSignInErrorMessage = (error) => {
    const statusCode = error?.response?.status;
    const responseMessage = String(error?.response?.data?.message ?? "").trim();

    if (statusCode === 401) {
      return responseMessage || "Invalid username or password.";
    }

    if (statusCode === 500) {
      return (
        responseMessage ||
        "We could not sign in , your account has been deactivated.  contact your administrator."
      );
    }
    
    if (responseMessage) {
      return responseMessage;
    }

    if (!error?.response) {
      return "Unable to reach the server. Please check your connection and try again.";
    }

    return "We could not sign in to this account right now. Please try again.";
  };

  const handleSignIn = async () => {
    setAlertMessage("");
    setUsernameError("");
    setPasswordError("");

    if (!username.trim()) {
      setUsernameError("Please enter username.");
      return;
    }

    if (!password.trim()) {
      setPasswordError("Please enter password.");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/api/login`,
        {
          username,
          password,
        },
        {
          headers: {
            "Cache-Control": "no-cache",
          },
        }
      );

      const {
        message,
        auth_key,
        name,
        last_login,
        preferred_language,
        state,
        admin,
        accountID,
        role,
        district_id,
        districtId,
        sub_division_id,
        subDivisionId,
        station_id,
        stationId,
        user,
      } = response.data;
      setAlertMessage(message);
      localStorage.setItem("lastLogin", last_login);
      localStorage.setItem("preferred_language", preferred_language);
      localStorage.setItem("authKey", auth_key);
      localStorage.setItem("userName", name);
      localStorage.setItem("userState", state);
      localStorage.setItem("admin", admin);
      localStorage.setItem("accountID", accountID);

      const sessionHierarchyValues = {
        role: role ?? user?.role ?? user?.user_role ?? "",
        district_id:
          district_id ?? user?.district_id ?? districtId ?? user?.districtId ?? "",
        sub_division_id:
          sub_division_id ??
          user?.sub_division_id ??
          subDivisionId ??
          user?.subDivisionId ??
          "",
        station_id:
          station_id ?? user?.station_id ?? stationId ?? user?.stationId ?? "",
      };

      Object.entries(sessionHierarchyValues).forEach(([key, value]) => {
        const normalizedValue = String(value ?? "").trim();

        if (normalizedValue) {
          localStorage.setItem(key, normalizedValue);
        } else {
          localStorage.removeItem(key);
        }
      });

      let nextMenuItems = [];

      try {
        nextMenuItems = await fetchUserMenu(auth_key);
      } catch (menuError) {
        console.error("Failed to fetch menu after login:", menuError);
      }

      onLogin(auth_key, name, last_login);
      navigate(getDefaultMenuPath(nextMenuItems) || LOGIN_REDIRECT_FALLBACK, {
        replace: true,
      });
    } catch (error) {
      console.error("Full error:", error);
      setAlertMessage(getSignInErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSignIn();
  };

  return (
    <div className="signin-container">
      <div className="bg-video">
        <video
          src="Untitled video - Made with Clipchamp (1).mp4"
          autoPlay
          muted
          loop
          playsInline
        />
      </div>

      <div className="login-container">
        <div className="col-60">
          <div className="image"></div>
        </div>
        <div className="col-40 content">
          <div className="login">
            <div className="login-title">
              <div>
                <img
                  src="/Main.png"
                  alt="Logo"
                  className="navbar-logos-login"
                />
              </div>

              {alertMessage && (
                <div className="login-alert login-alert--error" role="alert">
                  <FiAlertCircle aria-hidden="true" className="login-alert__icon" />
                  <span>{alertMessage}</span>
                </div>
              )}
            </div>
            <div className="form">
              <form onSubmit={handleSubmit}>
                <div className="form-table">
                  <input
                    type="text"
                    className={`form-controls ${
                      usernameError ? "is-invalid" : ""
                    }`}
                    placeholder="Enter username or email"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setAlertMessage("");
                      setUsernameError("");
                    }}
                  />
                  {usernameError && (
                    <div className="invalid-feedbacks">{usernameError}</div>
                  )}
                </div>

                <div className="form-table">
                  <div className="password-input-wrap">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`form-controls password-input ${
                        passwordError ? "is-invalid" : ""
                      }`}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setAlertMessage("");
                        setPasswordError("");
                      }}
                    />
                    <button
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="password-toggle"
                      onClick={() => setShowPassword((currentValue) => !currentValue)}
                      type="button"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {passwordError && (
                    <div className="invalid-feedbacks">{passwordError}</div>
                  )}
                </div>
                <div className="login-btn">
                  <button
                    type="submit"
                    className="btn btn-primary mt-3"
                    disabled={loading}
                  >
                    {loading ? <div className="load" /> : "Login"}
                  </button>
                </div>
              </form>
              <div className="login-end">
                <p>
                  <a href="/forgot-password" className="btn btn-link">
                    Forgot Password?
                  </a>
                </p>
                <p>
                  Don't have an account?<a href="/signup"> Sign up</a>
                </p>
              </div>
                 <div className="login-footer">
             <div className="para-footer">
                <div>
                  Developed By{" "}
                  <a
                    href="https://www.migarch.in/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    MigArch
                  </a>
                </div>
                {/* <p>Following on insta <a href="https://www.instagram.com/migarch_/" target="_blank" rel="noopener noreferrer"></a> and facebook <a href="https://www.facebook.com/mig7841/" target="_blank" rel="noopener noreferrer"></a></p> */}
              </div>
            <div className="social-icons">
              <p>
                Follow us on:
                  </p>
                <a
                  href="https://www.instagram.com/migarch_/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="icon-link"
                >
                  <img
                    src="/instagram.jpg"
                    alt="Logo"
                    className="navbar-logos-insta"
                  />
                </a>
                <a
                  href="https://www.facebook.com/mig7841/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="icon-link"
                >
                  <img
                    src="/Facebook.jpg"
                    alt="Logo"
                    className="navbar-logos-facebook"
                  />
                </a>
            
            </div>
            </div>
            </div>
        
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
